/* collection-paged.js — pagination pilot (dial-indicators, kolaouza).
 *
 * The section serves REAL Shopify pagination (36/page) on pilot collections, so
 * the page paints fast — but the client-side filter engine needs every product
 * in the DOM. This script closes that gap:
 *
 *   1. Background top-up: fetches the rest of the collection's cards from the
 *      existing hydration endpoint (?view=cards&page=N, 24/chunk, whole-collection
 *      pages) and inserts them in collection order, hidden behind .pgd-off.
 *      `productsLoaded` lets FilterManager register the cards and the filter
 *      snippets + hfb REAPPLIES re-run any ACTIVE filter over them — so results
 *      converge to correct even if someone filters mid-load. It fires per chunk
 *      only while a filter is active; otherwise once (end of the top-up, or the
 *      moment a filter becomes active), since unfiltered extra cards are hidden.
 *   2. Mode switching on `filterManager:update`:
 *        filtered  (visible < total)  → every card participates (.pgd-off lifted),
 *                                       pagination pills hidden — matches today's
 *                                       "filter shows all matches" behavior.
 *        unfiltered                   → only the server page's cards show,
 *                                       pagination pills back.
 *   3. Honesty veil: if a filter is active BEFORE the top-up finishes, the grid
 *      dims and a toast says the full collection is still loading — partial
 *      results are never presented as final.
 *
 * .pgd-off wins over FilterManager's element.style.display='' because it is a
 * class with !important — FM toggles inline style only.
 */
(function () {
  'use strict';

  var grid = document.querySelector('[data-productgrid-items][data-paged]');
  if (!grid || !window.FilterManager) return;

  var CHUNK = 24; // cards-chunk paginate size — keep in sync with sections/cards-chunk.liquid
  var perPage = parseInt(grid.dataset.paged, 10);
  var offset = parseInt(grid.dataset.pagedOffset, 10) || 0;
  var total = parseInt(grid.dataset.pagedCount, 10) || 0;
  var chunkPages = Math.ceil(total / CHUNK);

  var ready = false;
  var filtered = false;

  // --- collection-order index on the server-rendered cards -------------------
  var i = 0;
  Array.prototype.forEach.call(grid.querySelectorAll('[data-product-item]'), function (el) {
    el.dataset.gidx = String(offset + i++);
  });

  function onServerPage(gidx) { return gidx >= offset && gidx < offset + perPage; }

  function setOffState(el) {
    var g = parseInt(el.dataset.gidx, 10);
    el.classList.toggle('pgd-off', !filtered && !onServerPage(g));
  }

  // No wait-state UI at all (owner call 2026-08-10, after two rounds of toast
  // tuning): results fill in progressively while chunks arrive — each chunk
  // re-applies active filters, so an early filter's result set simply grows to
  // complete within a few seconds. The pagination pills are the only mode UI.
  function refreshMode() {
    Array.prototype.forEach.call(grid.querySelectorAll('[data-product-item]'), setOffState);
    document.querySelectorAll('.pagination--container').forEach(function (nav) {
      nav.classList.toggle('pgd-hidden', filtered);
    });
  }

  // Announcing new cards (rimg + productsLoaded) makes FilterManager register them and every
  // filter cell re-run: ~0.2 s at 4x mobile CPU per round on kolaouza. While no filter is active
  // the topped-up cards are hidden (.pgd-off), so they are announced ONCE, at the end of the
  // top-up or the moment a filter becomes active, and per chunk only while one is (WORK-241).
  var unannounced = false;
  function announce() {
    unannounced = false;
    document.dispatchEvent(new CustomEvent('theme:rimg:watch'));
    document.dispatchEvent(new CustomEvent('productsLoaded'));
  }

  document.addEventListener('filterManager:update', function (e) {
    var d = e.detail || {};
    var f = typeof d.visibleCount === 'number' && d.visibleCount < d.totalCount;
    if (f !== filtered) {
      filtered = f;
      if (filtered && unannounced) announce(); // register before .pgd-off lifts
      refreshMode();
    }
  });

  // A URL-seeded filter may have applied before this script ran — ask FM directly.
  var counts = window.FilterManager.getVisibleCounts();
  filtered = counts.total > 0 && counts.visible < counts.total;
  refreshMode();

  // --- background top-up ------------------------------------------------------
  var sortBy = new URLSearchParams(window.location.search).get('sort_by');

  function chunkUrl(page) {
    var p = new URLSearchParams();
    p.set('view', 'cards');
    p.set('page', String(page));
    if (sortBy) p.set('sort_by', sortBy);
    return window.location.pathname + '?' + p.toString();
  }

  // Inserts one chunk's cards in collection order. Cards and grid children are both in gidx
  // order, so one forward pass finds each insertion point, and a run of cards that lands before
  // the same child goes in as one fragment. A child without a gidx is never an insertion point
  // (same rule as before). Duplicates (cards the server page already rendered) are skipped by id.
  function insertChunk(cards, base) {
    var present = new Set();
    Array.prototype.forEach.call(grid.querySelectorAll('[data-product-id]'), function (el) {
      present.add(el.getAttribute('data-product-id'));
    });
    var kids = grid.children;
    var k = 0, run = null, runRef = null, added = 0;
    function flush() { if (run) { grid.insertBefore(run, runRef); run = null; } }
    for (var c = 0; c < cards.length; c++) {
      var card = cards[c];
      var id = card.getAttribute('data-product-id');
      if (id && present.has(id)) continue;
      var gidx = base + c;
      while (k < kids.length && !(parseInt(kids[k].dataset.gidx, 10) > gidx)) k++;
      var ref = kids[k] || null;
      if (run && ref !== runRef) flush();
      if (!run) { run = document.createDocumentFragment(); runRef = ref; }
      card.dataset.gidx = String(gidx);
      setOffState(card);
      run.appendChild(card);
      if (id) present.add(id);
      added++;
    }
    flush();
    return added;
  }

  // Parse and insert in separate tasks (each ~half of a 110-150 ms task at 4x mobile CPU), so
  // the top-up never holds the main thread long enough to delay a tap.
  function nextTask() { return new Promise(function (res) { setTimeout(res, 0); }); }

  function fetchChunk(page) {
    return fetch(chunkUrl(page), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var cards = Array.prototype.slice.call(doc.querySelectorAll('[data-product-item][data-product-id]'));
        return nextTask().then(function () {
          if (!insertChunk(cards, (page - 1) * CHUNK)) return;
          if (filtered) announce(); else unannounced = true;
        });
      })
      .catch(function () { /* chunk lost — finish() retries once below */ });
  }

  var attempt = 0;
  function finish() {
    if (unannounced) announce(); // one registration round for the whole sweep
    // Everything present? (count can drift a card or two vs products_count; accept ≥)
    var have = grid.querySelectorAll('[data-product-item]').length;
    if (have >= total || attempt >= 2) {
      ready = true;
      refreshMode();
      return;
    }
    attempt++;
    run(); // one retry sweep for failed chunks (present cards are skipped)
  }

  function run() {
    // Parallel pool (3 in flight): sequential fetching took the whole top-up to
    // 14s+ on slow mobile connections — the window where filters see a partial
    // collection should be as short as the network allows.
    var pages = [];
    for (var p = 1; p <= chunkPages; p++) {
      // skip chunks fully covered by the server page — nothing new in them
      var lo = (p - 1) * CHUNK, hi = p * CHUNK - 1;
      if (lo >= offset && hi < offset + perPage) continue;
      pages.push(p);
    }
    var next = 0;
    function worker() {
      if (next >= pages.length) return Promise.resolve();
      var page = pages[next++];
      return fetchChunk(page).then(worker);
    }
    Promise.all([worker(), worker(), worker()]).then(finish);
  }

  // Safety cap: the app-generated utility collections (orderlyemails ~2.6k,
  // globofilter index ~5.7k products) cross the paged threshold but render no
  // filter bar — background-fetching hundreds of chunks there is pure waste.
  // Real storefront collections top out well under this.
  if (total > 600) { ready = true; refreshMode(); return; }

  run();
})();
